import React, { useState, useEffect } from 'react';
import { Modal, Form, Col, Row, Button } from 'react-bootstrap';
import { Order, OrderService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { toast } from 'react-toastify';
import CurrencyFormatter from '../utils/currencyFormatter';
import {
    formatOrderCaptureStatus,
    getOrderCaptureStatusColor,
    formatCountdown,
    timestampToEpochSeconds,
    isOrderPendingCapture,
    isOrderCaptured,
    isOrderFailed,
    validateCaptureAmount,
} from '../utils/nayaxUtils';

interface NayaxCaptureActionsProps {
    order: Order;
    onActionComplete: () => void;
}

const NayaxCaptureActions: React.FC<NayaxCaptureActionsProps> = ({ order, onActionComplete }) => {
    const orderClient = useClient(OrderService);
    const [showCaptureModal, setShowCaptureModal] = useState(false);
    const [showVoidModal, setShowVoidModal] = useState(false);
    const [showRefundInfoModal, setShowRefundInfoModal] = useState(false);
    const [captureAmount, setCaptureAmount] = useState('');
    const [captureNote, setCaptureNote] = useState('');
    const [captureError, setCaptureError] = useState('');
    const [overageConfirmed, setOverageConfirmed] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isVoiding, setIsVoiding] = useState(false);
    const [countdown, setCountdown] = useState('');

    const capturableUntilSeconds = timestampToEpochSeconds(order.capturableUntil);
    const isPending = isOrderPendingCapture(order);
    const isCaptured = isOrderCaptured(order);
    const isFailed = isOrderFailed(order);
    const displayStatus = formatOrderCaptureStatus(order);
    const displayStatusColor = getOrderCaptureStatusColor(order);
    const preauthMajorUnits = order.paymentInformation
        ? Number(order.paymentInformation.preauthAmountCents) / 100
        : undefined;
    const parsedCaptureAmount = parseFloat(captureAmount);
    const hasValidCaptureAmount = captureAmount !== '' && !captureError && !isNaN(parsedCaptureAmount);
    const isCaptureOverPreauth = hasValidCaptureAmount
        && preauthMajorUnits !== undefined
        && parsedCaptureAmount > preauthMajorUnits;

    useEffect(() => {
        if (!isPending || capturableUntilSeconds === 0) return;

        const updateCountdown = () => {
            setCountdown(formatCountdown(capturableUntilSeconds));
        };
        updateCountdown();
        const interval = setInterval(updateCountdown, 60000);
        return () => clearInterval(interval);
    }, [isPending, capturableUntilSeconds]);

    const handleCaptureAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setCaptureAmount(val);
        setOverageConfirmed(false);
        const { valid, error } = validateCaptureAmount(val, preauthMajorUnits);
        setCaptureError(valid ? '' : (error ?? ''));
    };

    const handleCapture = async () => {
        const { valid, error } = validateCaptureAmount(captureAmount, preauthMajorUnits);
        if (!valid) {
            setCaptureError(error ?? 'Invalid amount');
            return;
        }
        if (isCaptureOverPreauth && !overageConfirmed) {
            return;
        }
        setIsCapturing(true);
        try {
            await orderClient.captureOrder({
                orderUuid: order.uuid,
                amountMajorUnits: captureAmount,
                note: captureNote,
            });
            toast.dismiss();
            toast.success(parseFloat(captureAmount) === 0 ? 'Void request sent to kiosk' : 'Capture request sent to kiosk');
            setShowCaptureModal(false);
            setCaptureAmount('');
            setCaptureNote('');
            setOverageConfirmed(false);
            onActionComplete();
        } catch (err: unknown) {
            toast.dismiss();
            toast.error(err instanceof Error ? err.message : 'Capture failed');
        } finally {
            setIsCapturing(false);
        }
    };

    const handleVoid = async () => {
        setIsVoiding(true);
        try {
            await orderClient.voidOrder({ orderUuid: order.uuid });
            toast.dismiss();
            toast.success('Void request sent to kiosk');
            setShowVoidModal(false);
            onActionComplete();
        } catch (err: unknown) {
            toast.dismiss();
            toast.error(err instanceof Error ? err.message : 'Void failed');
        } finally {
            setIsVoiding(false);
        }
    };

    if (!order.isNayaxOrder) return null;

    return (
        <>
            {/* Capture Status Badge */}
            {displayStatus && (
                <div className="mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${displayStatusColor}`}
                          data-testid="capture-status-badge">
                        {displayStatus}
                    </span>
                </div>
            )}

            {/* Countdown */}
            {isPending && countdown && (
                <div className="text-xs text-orange-600 mb-2" data-testid="capture-countdown">
                    Capture due: {countdown}
                </div>
            )}

            {/* Captured Amount */}
            {isCaptured && order.capturedAmountCents > BigInt(0) && (
                <div className="text-xs text-gray-600 mb-2" data-testid="captured-amount">
                    Captured: <CurrencyFormatter priceInCents={Number(order.capturedAmountCents)} />
                </div>
            )}

            {/* Failure Reason */}
            {isFailed && order.captureFailureReason && (
                <div className="text-xs text-red-600 mb-2" data-testid="capture-failure-reason">
                    Failed: {order.captureFailureReason}
                </div>
            )}

            {/* Action Buttons */}
            {isPending && (
                <div className="flex gap-2 mt-2">
                    <button
                        onClick={() => setShowCaptureModal(true)}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                        data-testid="capture-btn"
                        disabled={isCapturing || isVoiding}
                    >
                        Capture
                    </button>
                    <button
                        onClick={() => setShowVoidModal(true)}
                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors"
                        data-testid="void-btn"
                        disabled={isCapturing || isVoiding}
                    >
                        Void
                    </button>
                </div>
            )}

            {/* Refund info for captured Nayax orders */}
            {isCaptured && (
                <button
                    onClick={() => setShowRefundInfoModal(true)}
                    className="mt-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
                    data-testid="nayax-refund-info-btn"
                >
                    Refund Info
                </button>
            )}

            {/* Capture Modal */}
            <Modal show={showCaptureModal} onHide={() => setShowCaptureModal(false)} size="lg" data-testid="capture-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Capture Payment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-yellow-800 font-medium">
                            This is a one-time action. Once captured or voided, this cannot be changed.
                        </p>
                    </div>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={4}>Order UUID</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled value={order.uuid} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={4}>Pre-auth Amount</Form.Label>
                        <Col sm={8}>
                            <Form.Control type="text" disabled
                                value={order.paymentInformation
                                    ? `${Number(order.paymentInformation.preauthAmountCents) / 100}`
                                    : 'N/A'} />
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={4}>Capture Amount</Form.Label>
                        <Col sm={8}>
                            <Form.Control
                                type="text"
                                placeholder="0.00"
                                value={captureAmount}
                                onChange={handleCaptureAmountChange}
                                isInvalid={!!captureError}
                                data-testid="capture-amount-input"
                            />
                            <Form.Control.Feedback type="invalid">{captureError}</Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                Enter 0 to void instead of capturing.{preauthMajorUnits !== undefined && ` Pre-auth reference: ${preauthMajorUnits.toFixed(2)}`}
                            </Form.Text>
                        </Col>
                    </Form.Group>
                    {isCaptureOverPreauth && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3" data-testid="capture-overage-warning">
                            <p className="text-sm text-orange-900 font-medium mb-2">
                                Warning: capture amount is greater than the pre-authorized amount.
                            </p>
                            <p className="text-sm text-orange-800 mb-2">
                                If the second authorization is not successful, the risk is on the machine operator.
                            </p>
                            <p className="text-sm text-orange-800 mb-2">
                                The machine may also fail to charge the consumer above the Maximum Default Credit.
                            </p>
                            <Form.Check
                                id={`capture-overage-confirm-${order.uuid}`}
                                type="checkbox"
                                label="I understand the overage risks and want to continue."
                                checked={overageConfirmed}
                                onChange={(e) => setOverageConfirmed(e.target.checked)}
                                data-testid="capture-overage-confirm-checkbox"
                            />
                        </div>
                    )}
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label column sm={4}>Note (optional)</Form.Label>
                        <Col sm={8}>
                            <Form.Control
                                type="text"
                                placeholder="Optional capture note"
                                value={captureNote}
                                onChange={(e) => setCaptureNote(e.target.value)}
                            />
                        </Col>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCaptureModal(false)}>Cancel</Button>
                    <Button
                        variant="success"
                        onClick={handleCapture}
                        disabled={isCapturing || !!captureError || !captureAmount || (isCaptureOverPreauth && !overageConfirmed)}
                        data-testid="confirm-capture-btn"
                    >
                        {isCapturing ? 'Processing...' : (parseFloat(captureAmount || '0') === 0 ? 'Void (amount=0)' : 'Capture')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Void Confirmation Modal */}
            <Modal show={showVoidModal} onHide={() => setShowVoidModal(false)} data-testid="void-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Void</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-yellow-800 font-medium">
                            This is a one-time action. Voiding will release the pre-authorized hold on the customer's card.
                        </p>
                    </div>
                    <p>Are you sure you want to void this pre-authorization?</p>
                    <p className="text-sm text-gray-500 mt-2">Order: {order.uuid}</p>
                    {order.paymentInformation && (
                        <p className="text-sm text-gray-500">
                            Pre-auth: <CurrencyFormatter priceInCents={Number(order.paymentInformation.preauthAmountCents)} />
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVoidModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleVoid} disabled={isVoiding} data-testid="confirm-void-btn">
                        {isVoiding ? 'Processing...' : 'Void'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Nayax Refund Info Modal */}
            <Modal show={showRefundInfoModal} onHide={() => setShowRefundInfoModal(false)} data-testid="refund-info-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Nayax Refund Information</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <p className="text-sm text-blue-800 font-medium mb-2">
                            Post-capture refunds are not supported through this portal for Nayax transactions.
                        </p>
                        <p className="text-sm text-blue-700">
                            To process a refund, please use the Nayax portal or contact Nayax support directly.
                        </p>
                    </div>
                    <div className="space-y-2 text-sm">
                        <p><strong>Order UUID:</strong> {order.uuid}</p>
                        {order.paymentInformation?.paymentTransactionId && (
                            <p><strong>Transaction ID:</strong> {order.paymentInformation.paymentTransactionId}</p>
                        )}
                        {order.paymentInformation?.paymentDeviceSessionId && (
                            <p><strong>Session ID:</strong> {order.paymentInformation.paymentDeviceSessionId}</p>
                        )}
                        {order.paymentInformation?.rrn && (
                            <p><strong>RRN:</strong> {order.paymentInformation.rrn}</p>
                        )}
                        {order.capturedAmountCents > BigInt(0) && (
                            <p><strong>Captured Amount:</strong> <CurrencyFormatter priceInCents={Number(order.capturedAmountCents)} /></p>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRefundInfoModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NayaxCaptureActions;
